import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { VIP_CLIENTS } from '../constants/vipClients';
import { API_BASE } from '../config';
import { produccionApi, errorMessage } from '../utils/produccionApi';
import { formatKg } from '../utils/produccion';
import '../styles/picker.css';

function PickerV2Page() {
  const { logout, token, userRole } = useAuth();
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [returnCarts, setReturnCarts] = useState([]);
  // Production orders the frío pickers retire raw material for.
  const [prodOrders, setProdOrders] = useState([]);
  const [prodOrder, setProdOrder] = useState(null);
  const [prodNotice, setProdNotice] = useState('');
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
  };

  // Helper function to check if a client is VIP
  const isVIPClient = (clientName) => {
    return VIP_CLIENTS.some(vipClient =>
      vipClient.toLowerCase().trim() === clientName.toLowerCase().trim()
    );
  };

  // Helper function to sort pending orders with VIP priority
  const sortPendingOrders = (orders) => {
    return orders.sort((a, b) => {
      const aIsVIP = isVIPClient(a.client);
      const bIsVIP = isVIPClient(b.client);

      // VIP clients go first
      if (aIsVIP && !bIsVIP) return -1;
      if (!aIsVIP && bIsVIP) return 1;

      // If both are VIP or both are not VIP, sort by PO date (oldest first)
      const aDate = new Date(a.po_date);
      const bDate = new Date(b.po_date);
      return aDate - bDate;
    });
  };

  const filterOrdersByRole = (data) => {
    if (!Array.isArray(data)) return [];

    return data.filter(order => {
      if (activeTab === 'pending') {
        if (userRole === 'picker') {
          return order.status === 'pending' && order.item_status === false;
        } else if (userRole === 'frozen') {
          return order.status === 'pending' && order.frozen_status === false;
        }
      }
      return order.status === activeTab;
    });
  };

  // Carts of cancelled orders still waiting to go back to the shelves. Feeds
  // both the Anulados list and its count, so it loads whatever tab is open.
  const fetchReturnCarts = async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE}/cancelled-returns/`, {
        headers: { 'Authorization': `Token ${token}` },
      });
      const data = response.ok ? await response.json() : [];
      setReturnCarts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error:', error);
      setReturnCarts([]);
    }
  };

  const fetchProdOrders = async () => {
    if (!token || userRole !== 'frozen') return;
    const res = await produccionApi.get('retiro-orders/', token);
    setProdOrders(res.ok && Array.isArray(res.data) ? res.data : []);
  };

  const fetchOrders = async (status) => {
    if (!token) {
      console.error('No token available');
      return;
    }

    if (status === 'cancelled') {
      setIsLoading(true);
      await fetchReturnCarts();
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/get-filtered-pos/?status=${status}`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          const filteredData = filterOrdersByRole(data);
          // Apply VIP sorting only for pending orders
          const sortedData = status === 'pending' ? sortPendingOrders(filteredData) : filteredData;
          setOrders(sortedData);
        } else {
          console.error('Data received is not an array:', data);
          setOrders([]);
        }
      } else {
        console.error('Failed to fetch orders data', response.status, response.statusText);
        setOrders([]);
      }
    } catch (error) {
      console.error('Error:', error);
      setOrders([]);
    } finally {
      setIsLoading(false);
    }
  };

  const refresh = () => {
    fetchOrders(activeTab);
    if (activeTab !== 'cancelled') fetchReturnCarts();
    if (activeTab === 'pending') fetchProdOrders();
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleTabClick = (status) => {
    setActiveTab(status);
  };

  const handleItemClick = (order) => {
    if (activeTab === 'pending') {
      // Check if there's already a cart assigned for the user's role
      const hasAssignedCart = order.carts && order.carts.some(cart => {
        if (userRole === 'picker') {
          return cart.code.startsWith('CART-');
        } else if (userRole === 'frozen') {
          return cart.code.startsWith('BASKET-');
        }
        return false;
      });

      if (hasAssignedCart) {
        // If there's already a cart assigned for this role, navigate directly
        const assignedCart = order.carts.find(cart => {
          if (userRole === 'picker') {
            return cart.code.startsWith('CART-');
          } else if (userRole === 'frozen') {
            return cart.code.startsWith('BASKET-');
          }
          return false;
        });
        navigate(`/picker/${order.po_number}/${assignedCart.code}`, {
          state: { orderData: order }
        });
      } else {
        // If no cart is assigned for this role, show the modal
        setSelectedOrder(order);
        setOrderDetails(order);
        setShowModal(true);
      }
    } else {
      if (order.status === 'paid') {
        navigate(`/paid/${order.po_number}`);
      } else {
        navigate(`/qr-scanner/${order.po_number}`);
      }
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setOrderDetails(null);
    setSelectedOrder(null);
  };

  const proceedWithOrder = () => {
    closeModal();
    navigate(`/qr-scanner/${selectedOrder.po_number}`, {
      state: { orderData: selectedOrder }
    });
  };

  const goToRetiro = (order) => navigate(`/production/${order.po_number}/retiro`);

  // One person per order: someone else's stays closed, mine goes straight in.
  const handleProdClick = (order) => {
    setProdNotice('');
    if (order.holder && !order.mine) {
      setProdNotice(`La está retirando ${order.holder}.`);
    } else if (order.mine) {
      goToRetiro(order);
    } else {
      setProdOrder(order);
    }
  };

  const takeProdOrder = async () => {
    const order = prodOrder;
    const res = await produccionApi.send('POST', `orders/${encodeURIComponent(order.po_number)}/retiro/take/`, {}, token);
    if (res.ok) {
      goToRetiro(order);
      return;
    }
    setProdOrder(null);
    setProdNotice(errorMessage(res));
    fetchProdOrders();
  };

  const prodProducts = (order) => {
    const names = (order.products || []).map((p) => p.name);
    return names.length > 1 ? `${names[0]} +${names.length - 1}` : (names[0] || '');
  };

  const renderProdOrder = (order) => (
    <div
      key={order.po_number}
      className={`${getClassNames('home-item')} pending prod-order`}
      onClick={() => handleProdClick(order)}
    >
      <div className={getClassNames('home-number')}>{order.po_number}</div>
      <div className={getClassNames('home-info')}>
        <div className="client-header">
          <span className={getClassNames('home-client')}>{prodProducts(order)}</span>
          <span className="prod-badge">PROD</span>
        </div>
        <br />
        <span className={getClassNames('home-address')}>
          {order.lines.map((l) => `${l.sku.name} · ${formatKg(Number(l.planned))} kg`).join(' · ')}
        </span>
        {order.holder && <div className="prod-holder">{`Retirando: ${order.holder}`}</div>}
      </div>
      <div className={getClassNames('home-status-indicator')}></div>
    </div>
  );

  const handleCartCodeRedirect = () => {
    navigate('/cartcode');
  };

  // Get role-specific class names
  const getClassNames = (baseClass) => {
    const rolePrefix = userRole === 'frozen' ? 'frio' : 'picker';
    return `${rolePrefix}${baseClass}`;
  };

  const renderReturnCart = (cart) => (
    <div
      key={cart.cart_code}
      className={`${getClassNames('home-item')} cancelled`}
      onClick={() => navigate(`/devolucion/${cart.cart_code}`)}
    >
      <div className={getClassNames('home-number')}>{cart.po_number}</div>
      <div className={getClassNames('home-info')}>
        <div className="client-header">
          <span className={getClassNames('home-client')}>{cart.client}</span>
        </div>
        <br />
        <span className={getClassNames('home-address')}>
          {cart.cart_code} · {cart.pending_lines > 0
            ? `${cart.pending_lines} ${cart.pending_lines === 1 ? 'línea' : 'líneas'} por devolver`
            : 'Nada que devolver'}
        </span>
      </div>
      <div className={getClassNames('home-status-indicator')}></div>
    </div>
  );

  const listItems = activeTab === 'cancelled' ? returnCarts : orders;
  const shownProdOrders = activeTab === 'pending' ? prodOrders : [];

  return (
    <div className={getClassNames('home-container')}>
      <button
        className={getClassNames('home-refresh-button')}
        onClick={refresh}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 489.645 489.645"
          width="24"
          height="24"
          fill="#2c6e91"
        >
          <path d="M460.656,132.911c-58.7-122.1-212.2-166.5-331.8-104.1c-9.4,5.2-13.5,16.6-8.3,27c5.2,9.4,16.6,13.5,27,8.3 c99.9-52,227.4-14.9,276.7,86.3c65.4,134.3-19,236.7-87.4,274.6c-93.1,51.7-211.2,17.4-267.6-70.7l69.3,14.5 c10.4,2.1,21.8-4.2,23.9-15.6c2.1-10.4-4.2-21.8-15.6-23.9l-122.8-25c-20.6-2-25,16.6-23.9,22.9l15.6,123.8 c1,10.4,9.4,17.7,19.8,17.7c12.8,0,20.8-12.5,19.8-23.9l-6-50.5c57.4,70.8,170.3,131.2,307.4,68.2 C414.856,432.511,548.256,314.811,460.656,132.911z" />
        </svg>
      </button>

      <button className={getClassNames('home-logout-button')} onClick={handleLogout}>
        Cerrar Sesión
      </button>

      <h1 className={getClassNames('home-heading')}>
        {userRole === 'frozen' ? 'Pedidos Fríos' : 'Pedidos'}
      </h1>

      <div className="button-group">
        <button
          className={getClassNames('home-cartcode-button')}
          onClick={handleCartCodeRedirect}
        >
          Identificador
        </button>
        {userRole === 'picker' && (
          <button
            className={getClassNames('home-cartcode-button')}
            onClick={() => navigate('/admin/test-scanner')}
          >
            Test Scanner
          </button>
        )}
      </div>

      <div className={getClassNames('home-tabs')}>
        <button
          className={`${getClassNames('home-tab')} ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => handleTabClick('pending')}
        >
          Pendientes
        </button>
        <button
          className={`${getClassNames('home-tab')} ${activeTab === 'paid' ? 'active' : ''}`}
          onClick={() => handleTabClick('paid')}
        >
          Pagados
        </button>
        <button
          className={`${getClassNames('home-tab')} ${activeTab === 'cancelled' ? 'active' : ''}`}
          onClick={() => handleTabClick('cancelled')}
        >
          Anulados{returnCarts.length > 0 ? ` (${returnCarts.length})` : ''}
        </button>
      </div>

      {prodNotice && activeTab === 'pending' && <div className="prod-notice">{prodNotice}</div>}

      <div className={getClassNames('home-list')}>
        {isLoading ? (
          <div className={getClassNames('home-loading')}>
            <div className="loading-spinner"></div>
            <p>Cargando pedidos...</p>
          </div>
        ) : listItems.length > 0 || shownProdOrders.length > 0 ? (
          activeTab === 'cancelled' ? returnCarts.map(renderReturnCart) : shownProdOrders.map(renderProdOrder).concat(orders.map((order, index) => {
            const isVIP = activeTab === 'pending' ? isVIPClient(order.client) : false;
            return order && order.po_number ? (
              <div
                key={order.po_number || index}
                className={`${getClassNames('home-item')} ${order.status || 'default'} ${isVIP ? 'vip-client' : ''}`}
                onClick={() => handleItemClick(order)}
              >
                <div className={getClassNames('home-number')}>{order.po_number}</div>
                <div className={getClassNames('home-info')}>
                  <div className="client-header">
                    <span className={getClassNames('home-client')}>{order.client || 'No client'}</span>
                    {isVIP && <span className="vip-badge">VIP</span>}
                  </div>
                  <br />
                  <span className={getClassNames('home-address')}>{order.address || 'No address'}</span>
                </div>
                <div className={getClassNames('home-status-indicator')}></div>
              </div>
            ) : null;
          }))
        ) : (
          <div className={getClassNames('home-no-orders')}>
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
              <polyline points="13 2 13 9 20 9"></polyline>
            </svg>
            <p>No hay pedidos {userRole === 'frozen' ? 'fríos ' : ''}{activeTab === 'pending' ? 'pendientes' : activeTab === 'paid' ? 'pagados' : 'anulados'} en este momento.</p>
            <p className={`${getClassNames('home-no-orders-sub')}`}>Intente refrescar la página o vuelva más tarde.</p>
          </div>
        )}
      </div>

      {prodOrder && (
        <div className={`${getClassNames('home-modal-overlay')}`} onClick={() => setProdOrder(null)}>
          <div className={`${getClassNames('home-modal')}`} onClick={e => e.stopPropagation()}>
            <div className={`${getClassNames('home-modal-header')}`}>
              <h2>{`Orden de producción ${prodOrder.po_number}`}</h2>
              <button className={`${getClassNames('home-modal-close')}`} onClick={() => setProdOrder(null)}>×</button>
            </div>
            <div className={`${getClassNames('home-modal-content')}`}>
              <div className={`${getClassNames('home-modal-info')}`}>
                <p><strong>Producto:</strong> {(prodOrder.products || []).map((p) => p.name).join(', ')}</p>
                <div className="packing-info">
                  <span className="modal-packing-badge">🏭 PROD</span>
                </div>
              </div>
              <div className={`${getClassNames('home-modal-products')}`}>
                <h3>Materia prima</h3>
                {prodOrder.lines.map((line) => (
                  <div key={line.sku.id} className={`${getClassNames('home-modal-product-item')}`}>
                    <div className="product-info">
                      <span className="product-name">{line.sku.name}</span>
                      <span className="product-sku">SKU: {line.sku.code}</span>
                    </div>
                    <div className="product-quantities">
                      <span className="product-quantity">{`${formatKg(Number(line.planned))} kg`}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className={`${getClassNames('home-modal-footer')}`}>
              <button className={`${getClassNames('home-modal-button')} secondary`} onClick={() => setProdOrder(null)}>Cancelar</button>
              <button className={`${getClassNames('home-modal-button')} primary`} onClick={takeProdOrder}>Retirar</button>
            </div>
          </div>
        </div>
      )}

      {showModal && orderDetails && (
        <div className={`${getClassNames('home-modal-overlay')}`} onClick={closeModal}>
          <div className={`${getClassNames('home-modal')}`} onClick={e => e.stopPropagation()}>
            <div className={`${getClassNames('home-modal-header')}`}>
              <h2>Detalles del Pedido {selectedOrder?.po_number}</h2>
              <button className={`${getClassNames('home-modal-close')}`} onClick={closeModal}>×</button>
            </div>
            <div className={`${getClassNames('home-modal-content')}`}>
              <div className={`${getClassNames('home-modal-info')}`}>
                <p><strong>Cliente:</strong> {selectedOrder?.client}</p>
                <p><strong>Dirección:</strong> {selectedOrder?.address}</p>
                <p><strong>Fecha:</strong> {new Date(orderDetails?.po_date).toLocaleDateString('es-ES', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}</p>
                {selectedOrder?.packing && (
                  <div className="packing-info">
                    <span className="modal-packing-badge">
                      📦 EMBALAR
                    </span>
                  </div>
                )}
              </div>
              <div className={`${getClassNames('home-modal-products')}`}>
                <h3>Productos</h3>
                {orderDetails.items?.map((item, index) => (
                  <div key={index} className={`${getClassNames('home-modal-product-item')}`}>
                    <div className="product-info">
                      <span className="product-name">{item.sku_name}</span>
                      <span className="product-sku">SKU: {item.sku_code}</span>
                    </div>
                    <div className="product-quantities">
                      <span className="product-quantity">Pedido: {item.ordered_quantity} {item.format_unit_kg ? ' kg' : ''}</span>
                      <span className="product-format">
                        (
                          {item.format_quantity}
                          {item.format_unit_kg ? ' kg' : ' unidad'}
                          {' x '}
                          {item.format_name}
                        )
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className={`${getClassNames('home-modal-footer')}`}>
              <button className={`${getClassNames('home-modal-button')} secondary`} onClick={closeModal}>Cancelar</button>
              <button className={`${getClassNames('home-modal-button')} primary`} onClick={proceedWithOrder}>Proceder</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PickerV2Page;
